<template>
    <div class="bottom-up-product-summary-table">
        <MoatTable
            v-if="axisRange.length > 0"
            class="w-full h-full"
            :key="`concepts_${tableUpdater}`"
            :class="themeClass"
            :column-defs="colDefs"
            :tooltip-interaction="true"
            :tooltip-mouse-track="true"
            :tooltip-show-delay="500"
            tooltip-show-mode="whenTruncated"
            :autoGroupColumnDef="autoGroupColumnDef"
            :groupRemoveLowestSingleChildren="true"
            :groupDisplayType="groupDisplayType"
            :treeData="true"
            :getDataPath="getDataPath"
            :autoSizeStrategy="sizingStrategy"
            :row-data="props.tableData"
            @grid-ready="gridInit"
            @first-data-rendered="getMaxCount"
            @cell-clicked="onCellClicked"
        />
        <Teleport to="#aon-app .main-content">
            <PatentList
                v-if="showPatentList"
                :passedFilters="initialPatentListFilter"
                @close-patent-list="showPatentList = false"
            />
        </Teleport>
    </div>
</template>

<script setup>
import { ref, inject, onBeforeMount, nextTick, computed } from 'vue'
import { useEntityStore, usePatentViewerStore } from '@/stores'
import { useI18n } from 'vue-i18n'
import { AgGridVue as MoatTable } from '@ag-grid-community/vue3'

import { useFlag } from '@unleash/proxy-client-vue'
import PatentList from '@/components/patentViewer/PatentList.vue'

const isProUser = useFlag('ipAlpha.proUsers')
const allowPatentList = computed(() => {
    return isProUser.value
})
const patentListViewType = ref('conceptIntersection')
const showPatentList = ref(false)
const initialPatentListFilter = ref({
    jurisdiction: 'US',
    legalStatus: ['Active'],
})

// injectors
const filters = inject('filters')
const { t } = useI18n()

const entityStore = useEntityStore()
const patentViewerStore = usePatentViewerStore()

const props = defineProps({
    tableData: {
        type: Array,
        default: () => [],
    },
})

const gridApi = ref(null)
const tableUpdater = ref(0)
const getDataPath = ref(null)
const sizingStrategy = ref({
    type: 'fitGridWidth',
    // width: 1000,
})
const axisRange = ref([0, 10])
const colDefs = ref([
    {
        headerComponent: 'MTCustomColHeader',
        headerComponentParams: {
            headerName: 'Innovation Score',
        },
        headerTooltip: 'Innovation Score',
        field: 'innovationScore',
        valueFormatter: (p) => {
            if (p.data) {
                return p.data.innovationScore.toLocaleString()
            }
        },
        flex: 1,
        minWidth: 180,
    },
    {
        headerComponent: 'MTCustomColHeader',
        headerComponentParams: {
            headerName: 'Patent Count',
        },
        field: 'patentCount',
        headerTooltip: 'Patent Count',
        flex: 1,
        minWidth: 180,
        cellRenderer: 'MTCustomCellLinkWContext',
        cellRendererParams: {
            allowClick: allowPatentList.value,
            cellClick: async (params) => {
                patentViewerStore.nodeIdList = [...params.data.hierarchyItems]
                patentViewerStore.technologyName = params.data.conceptName
                if (entityStore.entity) {
                    patentViewerStore.entityPkList.push(entityStore.entity.aon_entity_pk)
                    patentViewerStore.entityName = entityStore.entity.name
                }
                patentViewerStore.listViewType = patentListViewType.value
                await nextTick()
                showPatentList.value = true
            },
        },
        valueFormatter: (p) => {
            if (p.data) {
                return p.data.patentCount.toLocaleString()
            }
        },
    },
    {
        suppressHeaderMenuButton: true,
        cellRenderer: 'MTContextMenu',
        cellRendererParams: {
            actionsList: [
                isProUser.value
                    ? {
                          name: 'View Patents',
                          icon: 'fa-square-list',
                          actionName: 'patents',
                          patentsAction: async (params) => {
                              patentViewerStore.nodeIdList = [...params.data.hierarchyItems]
                              patentViewerStore.technologyName = params.data.conceptName
                              if (entityStore.entity) {
                                  patentViewerStore.entityPkList.push(
                                      entityStore.entity.aon_entity_pk
                                  )
                                  patentViewerStore.entityName = entityStore.entity.name
                              }
                              patentViewerStore.listViewType = patentListViewType.value
                              await nextTick()
                              showPatentList.value = true
                          },
                      }
                    : null,
            ],
            highlightLabel: true,
        },
        pinned: 'right',
        width: 42,
    },
])
const autoGroupColumnDef = ref(null)
const groupDisplayType = ref(null)
const themeClass = ref('ag-theme-quartz')

onBeforeMount(() => {
    defineTableGroups()
    getDataPath.value = (data) => {
        return data.hierarchyItems
    }
    groupDisplayType.value = 'singleColumn'
})

const gridInit = (params) => {
    gridApi.value = params.api
    params.api.hideOverlay()
}

const getMaxCount = () => {
    const max = Math.max(...props.tableData.map((item) => item.patentCount))
    axisRange.value = [0, max]
    gridApi.value.setGridOption('columnDefs', colDefs.value)
}

const onCellClicked = async (params) => {}

const defineTableGroups = () => {
    autoGroupColumnDef.value = {
        field: 'conceptName',
        tooltipField: 'conceptName',
        suppressMovable: true,
        lockPosition: 'left',
        headerName: 'Product',
        cellRendererParams: {
            suppressCount: true,
        },
        cellClass: (params) => {},
        minWidth: 400,
        pinned: 'left',
    }
}
</script>
<style lang="scss" scoped>
.bottom-up-product-summary-table {
    width: 100%;
    height: 100%;
    position: relative;
}
</style>
